import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../utils/api-config";
import AssignmentService from "../services/AssignmentService";
import type { CreateAssignmentDto } from "../services/AssignmentService";
import DriverService from "../services/DriverService";
import type { Driver } from "../services/DriverService";
import VehicleService from "../services/VehicleService";
import type { Vehicle } from "../services/VehicleService";
import HandlingEquipmentService from "../services/HandlingEquipmentService";
import type { HandlingEquipment } from "../services/HandlingEquipmentService";
import { useSnackbar } from "notistack";
import BackButton from "../components/ui/BackButton";
import useRecentItems from "../hooks/useRecentItems";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Alert,
  Tooltip,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  useTheme,
  useMediaQuery,
  Container,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Fade,
  Zoom,
  LinearProgress,
} from "@mui/material";
import { X, QrCode } from "lucide-react";
import {
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Speed as SpeedIcon,
  LocalGasStation as FuelLevelIcon,
  People as PeopleIcon,
  DirectionsCar as CarIcon,
} from "@mui/icons-material";
import {
  canDriverOperateVehicle,
  VEHICLE_TYPE_LABELS,
  LICENSE_TYPE_LABELS,
  type VehicleType,
  type LicenseType,
} from "../config/licenseVehicleMapping";
import QRCodeScanner from "../components/QRCodeScanner";

// Mapping des types d'équipements vers leurs images
const EQUIPMENT_IMAGES: Record<string, string> = {
  diable: "/equipements/diable.png",
  chariot_plateforme: "/equipements/chariot-plateforme.png",
  chariot_ridelles: "/equipements/chariot-ridelles.png",
  plateau_roulant: "/equipements/plateau-roulant.png",
  transpalette_manuel: "/equipements/transpalette-manuel.png",
  transpalette_electrique: "/equipements/transpalette-electrique.png",
  chariot_elevateur: "/equipements/chariot-elevateur.png",
};

const AssignmentForm = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [searchParams] = useSearchParams();
  const plateFromUrl = searchParams.get("plate");
  const driverIdFromUrl = searchParams.get("driverId");
  const { enqueueSnackbar } = useSnackbar();
  const { addRecentVehicle, addRecentDriver, recentItems } = useRecentItems();

  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [vehiclePreselected, setVehiclePreselected] = useState(false);
  const [driverPreselected, setDriverPreselected] = useState(false);
  
  // États pour le scan QR
  const [vehicleScanOpen, setVehicleScanOpen] = useState(false);
  const [driverScanOpen, setDriverScanOpen] = useState(false);

  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  // Filtres et tris
  const [vehicleFilterStatus, setVehicleFilterStatus] = useState<
    "all" | "available"
  >("all");
  const [vehicleSort, setVehicleSort] = useState<"plate" | "brand">("plate");

  const [driverFilterStatus, setDriverFilterStatus] = useState<
    "all" | "available"
  >("all");
  const [driverSort, setDriverSort] = useState<"name" | "id">("name");

  // États pour les équipements de manutention
  const [equipments, setEquipments] = useState<HandlingEquipment[]>([]);
  const [loadingEquipments, setLoadingEquipments] = useState(false);
  const [showEquipmentSection, setShowEquipmentSection] = useState(false);
  const [selectedEquipment, setSelectedEquipment] =
    useState<HandlingEquipment | null>(null);

  // Fonction pour obtenir la date/heure actuelle
  const getCurrentDateTime = () => {
    const now = new Date();
    return {
      date: now.toISOString().split("T")[0],
      time: now.toTimeString().slice(0, 5),
    };
  };

  // Fonction pour convertir le niveau de carburant backend vers pourcentage
  const convertFuelLevelToPercentage = (fuelLevel: string | number | undefined): number => {
    if (!fuelLevel) return 100;
    
    // Si c'est déjà un nombre (pourcentage)
    if (typeof fuelLevel === 'number') {
      return fuelLevel;
    }
    
    // Si c'est une chaîne, convertir depuis le format backend
    const fuelStr = String(fuelLevel).trim();
    switch (fuelStr) {
      case "1/4":
        return 25;
      case "1/3":
        return 33;
      case "1/2":
        return 50;
      case "2/3":
        return 67;
      case "3/4":
        return 75;
      case "Plein":
      case "plein":
        return 100;
      default:
        // Essayer de parser comme nombre
        const num = parseFloat(fuelStr);
        if (!isNaN(num)) {
          return num;
        }
        return 100; // Par défaut
    }
  };

  const [formData, setFormData] = useState<CreateAssignmentDto>(() => {
    const now = getCurrentDateTime();
    return {
    vehiclePlate: "",
    driverId: "",
    driverFirstName: "",
    driverLastName: "",
      date: now.date,
      time: now.time,
    startMileage: 0,
    startFuelLevel: 100,
    purpose: "",
    };
  });

  useEffect(() => {
    fetchDrivers();
    fetchVehicles();
    fetchEquipments();
    
    // Mettre à jour la date/heure à l'ouverture du formulaire
    const now = getCurrentDateTime();
    setFormData((prev) => ({
      ...prev,
      date: now.date,
      time: now.time,
    }));
  }, []);

  // Pré-sélection automatique du véhicule depuis l'URL (QR Code scan)
  useEffect(() => {
    const preselectVehicle = async () => {
    if (plateFromUrl && vehicles.length > 0 && !vehiclePreselected) {
      const vehicleToSelect = vehicles.find(
        (v) => v.plate.toUpperCase() === plateFromUrl.toUpperCase()
      );
      if (vehicleToSelect) {
        setSelectedVehicle(vehicleToSelect);
          
          // Récupérer l'historique pour le kilométrage et carburant
          let smartStartMileage = vehicleToSelect.mileage ? Number(vehicleToSelect.mileage) : 0;
          let smartStartFuelLevel = 100;
          
          try {
            const response = await axios.get(`${API_URL}/vehicles/${vehicleToSelect.plate}/history`);
            const history = response.data;
            const lastAssignment = history.assignments
              ?.filter((a: any) => a.status === "terminée")
              ?.sort((a: any, b: any) => 
                new Date(b.endDate || b.date).getTime() - new Date(a.endDate || a.date).getTime()
              )[0];
            
            if (lastAssignment) {
              if (lastAssignment.endMileage) smartStartMileage = lastAssignment.endMileage;
              if (lastAssignment.endFuelLevel) {
                smartStartFuelLevel = convertFuelLevelToPercentage(lastAssignment.endFuelLevel);
              }
            }
          } catch (err) {
            console.log("Impossible de récupérer l'historique pour pré-remplissage");
          }
          
          const now = getCurrentDateTime();
        setFormData((prev) => ({
          ...prev,
          vehiclePlate: vehicleToSelect.plate,
            date: now.date,
            time: now.time,
            startMileage: smartStartMileage,
            startFuelLevel: smartStartFuelLevel,
        }));
        setVehiclePreselected(true);
          enqueueSnackbar(`🚗 Véhicule ${vehicleToSelect.plate} pré-sélectionné (QR)`, {
          variant: "info",
        });
      }
    }
    };
    preselectVehicle();
  }, [plateFromUrl, vehicles, vehiclePreselected, enqueueSnackbar]);

  // Pré-sélection automatique du chauffeur depuis l'URL (QR Code scan)
  useEffect(() => {
    if (driverIdFromUrl && drivers.length > 0 && !driverPreselected) {
      const driverToSelect = drivers.find(
        (d) => d.driverId === driverIdFromUrl || d._id === driverIdFromUrl
      );
      if (driverToSelect) {
        setSelectedDriver(driverToSelect);
        setFormData((prev) => ({
          ...prev,
          driverId: driverToSelect.driverId,
          driverFirstName: driverToSelect.firstName,
          driverLastName: driverToSelect.lastName,
        }));
        setDriverPreselected(true);
        enqueueSnackbar(`👤 Chauffeur ${driverToSelect.firstName} ${driverToSelect.lastName} pré-sélectionné (QR)`, {
          variant: "info",
        });
      }
    }
  }, [driverIdFromUrl, drivers, driverPreselected, enqueueSnackbar]);

  const fetchDrivers = async () => {
    try {
      setLoadingDrivers(true);
      const data = await DriverService.getAll();
      // On récupère tous les chauffeurs pour permettre le filtrage côté client
      setDrivers(data);
    } catch (err) {
      console.error("Erreur lors du chargement des chauffeurs:", err);
      enqueueSnackbar("Erreur lors du chargement des chauffeurs", {
        variant: "error",
      });
    } finally {
      setLoadingDrivers(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      setLoadingVehicles(true);
      const data = await VehicleService.getAll();
      setVehicles(data);
    } catch (err) {
      console.error("Erreur lors du chargement des véhicules:", err);
      enqueueSnackbar("Erreur lors du chargement des véhicules", {
        variant: "error",
      });
    } finally {
      setLoadingVehicles(false);
    }
  };

  const fetchEquipments = async () => {
    try {
      setLoadingEquipments(true);
      const data = await HandlingEquipmentService.getAll();
      // Filtrer uniquement les équipements disponibles (state ou operationalState)
      setEquipments(
        data.filter(
          (e: any) =>
            e.state === "disponible" || e.operationalState === "disponible"
        )
      );
    } catch (err) {
      console.error("Erreur lors du chargement des équipements:", err);
    } finally {
      setLoadingEquipments(false);
    }
  };

  const filteredVehicles = useMemo(() => {
    let result = [...vehicles];

    // Filtre
    if (vehicleFilterStatus === "available") {
      result = result.filter((v) => v.operationalState === "disponible");
    }

    // Tri
    result.sort((a, b) => {
      if (vehicleSort === "plate") {
        return a.plate.localeCompare(b.plate);
      } else {
        // Tri par marque puis modèle
        const brandCompare = a.brand.localeCompare(b.brand);
        if (brandCompare !== 0) return brandCompare;
        return a.model.localeCompare(b.model);
      }
    });

    return result;
  }, [vehicles, vehicleFilterStatus, vehicleSort]);

  // Vérifier si un chauffeur est compatible avec le véhicule sélectionné
  const isDriverCompatible = (driver: Driver): boolean => {
    if (!selectedVehicle?.vehicleType) return true;
    return canDriverOperateVehicle(
      driver.licenseType as LicenseType,
      selectedVehicle.vehicleType as VehicleType
    );
  };

  const filteredDrivers = useMemo(() => {
    let result = [...drivers];

    // Filtre
    if (driverFilterStatus === "available") {
      result = result.filter((d) => d.status === "disponible");
    }

    // Tri - les chauffeurs compatibles en premier
    result.sort((a, b) => {
      // D'abord par compatibilité (compatibles en premier)
      const aCompatible = isDriverCompatible(a);
      const bCompatible = isDriverCompatible(b);
      if (aCompatible && !bCompatible) return -1;
      if (!aCompatible && bCompatible) return 1;

      // Ensuite par le critère choisi
      if (driverSort === "name") {
        const nameA = `${a.lastName} ${a.firstName}`;
        const nameB = `${b.lastName} ${b.firstName}`;
        return nameA.localeCompare(nameB);
      } else {
        const idA = a.driverId || "";
        const idB = b.driverId || "";
        return idA.localeCompare(idB);
      }
    });

    return result;
  }, [drivers, driverFilterStatus, driverSort, selectedVehicle]);

  const handleDriverChange = (_event: any, newValue: Driver | null) => {
    setSelectedDriver(newValue);
    if (newValue) {
      if (newValue.status !== "disponible") {
        enqueueSnackbar(
          `Attention : Ce chauffeur n'est pas disponible (${newValue.status})`,
          { variant: "warning" }
        );
      }

      // Vérifier la compatibilité permis/véhicule
      if (selectedVehicle?.vehicleType && !isDriverCompatible(newValue)) {
        const vehicleLabel =
          VEHICLE_TYPE_LABELS[selectedVehicle.vehicleType as VehicleType] ||
          selectedVehicle.vehicleType;
        const licenseLabel =
          LICENSE_TYPE_LABELS[newValue.licenseType as LicenseType] ||
          `Permis ${newValue.licenseType}`;
        enqueueSnackbar(
          `⚠️ ${newValue.firstName} ${newValue.lastName} ne peut pas conduire ce véhicule. Son ${licenseLabel} ne permet pas de conduire un ${vehicleLabel}.`,
          { variant: "error", autoHideDuration: 6000 }
        );
        // Empêcher la sélection
        setSelectedDriver(null);
        return;
      }

      setFormData((prev) => ({
        ...prev,
        driverId: newValue.driverId || newValue._id,
        driverFirstName: newValue.firstName,
        driverLastName: newValue.lastName,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        driverId: "",
        driverFirstName: "",
        driverLastName: "",
      }));
    }
  };

  const handleVehicleChange = async (_event: any, newValue: Vehicle | null) => {
    setSelectedVehicle(newValue);
    if (newValue) {
      // ✨ PRÉ-REMPLISSAGE INTELLIGENT
      let smartStartMileage = newValue.mileage ? Number(newValue.mileage) : 0;
      let smartStartFuelLevel = 100;

      try {
        // Récupérer l'historique du véhicule pour pré-remplir intelligemment
        const response = await axios.get(
          `${API_URL}/vehicles/${newValue.plate}/history`
        );
        const history = response.data;

        // Trouver la dernière affectation terminée
        const lastAssignment = history.assignments
          ?.filter((a: any) => a.status === "terminée")
          ?.sort(
            (a: any, b: any) =>
              new Date(b.endDate || b.date).getTime() -
              new Date(a.endDate || a.date).getTime()
          )[0];

        if (lastAssignment) {
          // Utiliser le kilométrage de fin de la dernière affectation
          if (lastAssignment.endMileage) {
            smartStartMileage = lastAssignment.endMileage;
          }
          // Utiliser le niveau de carburant de fin (convertir en pourcentage)
          if (
            lastAssignment.endFuelLevel !== undefined &&
            lastAssignment.endFuelLevel !== null
          ) {
            smartStartFuelLevel = convertFuelLevelToPercentage(lastAssignment.endFuelLevel);
          }
        }
      } catch (err) {
        console.log(
          "Impossible de récupérer l'historique, utilisation des valeurs par défaut"
        );
      }

      // Mettre à jour avec date/heure actuelle + données du véhicule
      const now = getCurrentDateTime();
      setFormData((prev) => ({
        ...prev,
        vehiclePlate: newValue.plate,
        date: now.date, // Toujours à jour
        time: now.time, // Toujours à jour
        startMileage: smartStartMileage,
        startFuelLevel: smartStartFuelLevel,
      }));

      // Afficher un message informatif
      if (smartStartMileage > 0) {
        enqueueSnackbar(
          `✅ Données pré-remplies depuis la dernière affectation : ${smartStartMileage.toLocaleString()} km, carburant ${smartStartFuelLevel}%`,
          { variant: "info", autoHideDuration: 4000 }
        );
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        vehiclePlate: "",
        startMileage: 0,
        startFuelLevel: 100,
      }));
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "startMileage" || name === "startFuelLevel"
          ? Number(value)
          : value,
    }));
  };

  // Handler pour le scan de plaque véhicule
  const handleVehiclePlateScanned = (plate: string) => {
    setVehicleScanOpen(false);
    const vehicleToSelect = vehicles.find(
      (v) => v.plate.toUpperCase() === plate.toUpperCase()
    );
    if (vehicleToSelect) {
      handleVehicleChange(null, vehicleToSelect);
      enqueueSnackbar(`🚗 Véhicule ${plate} scanné et sélectionné`, { variant: "success" });
    } else {
      enqueueSnackbar(`❌ Véhicule ${plate} non trouvé`, { variant: "error" });
    }
  };

  // Handler pour le scan d'ID chauffeur
  const handleDriverIdScanned = (scannedId: string) => {
    setDriverScanOpen(false);
    const driverToSelect = drivers.find(
      (d) => d.driverId === scannedId || d._id === scannedId
    );
    if (driverToSelect) {
      handleDriverChange(null, driverToSelect);
      enqueueSnackbar(`👤 Chauffeur ${driverToSelect.firstName} ${driverToSelect.lastName} scanné`, { variant: "success" });
    } else {
      enqueueSnackbar(`❌ Chauffeur ${scannedId} non trouvé`, { variant: "error" });
    }
  };

  const isFormValid = () => {
    // Vérifier si le véhicule est disponible
    const isVehicleAvailable =
      selectedVehicle?.operationalState === "disponible";

    return (
      !!formData.vehiclePlate &&
      !!formData.driverId &&
      !!formData.driverFirstName &&
      !!formData.driverLastName &&
      !!formData.date &&
      !!formData.time &&
      isVehicleAvailable // Bloquer si le véhicule n'est pas disponible
    );
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      // Préparer les données avec l'équipement optionnel
      const submitData: any = { ...formData };

      if (selectedEquipment) {
        submitData.equipment = {
          taken: true,
          returned: false,
          equipmentId: selectedEquipment._id,
          equipmentType: selectedEquipment.equipmentType,
          equipmentNumber: selectedEquipment.internalNumber,
          equipmentBrand: selectedEquipment.brand,
          equipmentModel: selectedEquipment.model,
        };
      }

      const response = await AssignmentService.create(submitData);

      // Sauvegarder les éléments récents pour l'auto-complétion
      if (selectedVehicle) {
        addRecentVehicle({
          plate: selectedVehicle.plate,
          brand: selectedVehicle.brand,
          model: selectedVehicle.model,
        });
      }
      if (selectedDriver) {
        addRecentDriver({
          id: selectedDriver._id,
          name: `${selectedDriver.firstName} ${selectedDriver.lastName}`,
        });
      }

      enqueueSnackbar("Affectation créée avec succès", { variant: "success" });
      navigate(`/inspection-exterieure/${response._id}`);
    } catch (err) {
      console.error("Erreur lors de la création:", err);
      enqueueSnackbar("Erreur lors de la création de l'affectation", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getVehicleStateLabel = (state: string) => {
    switch (state) {
      case "disponible":
        return { label: "Disponible", color: "success" as const };
      case "en_service":
        return { label: "En service", color: "error" as const };
      case "maintenance":
        return { label: "Maintenance", color: "warning" as const };
      case "parking":
        return { label: "Hors service", color: "info" as const };
      case "archivé":
        return { label: "Archivé", color: "default" as const };
      default:
        return { label: state, color: "default" as const };
    }
  };

  const getDriverStateLabel = (state: string) => {
    switch (state) {
      case "disponible":
        return { label: "Disponible", color: "success" as const };
      case "en_mission":
        return { label: "En mission", color: "warning" as const }; // Supposons 'en_mission' ou autre
      case "congés":
        return { label: "Congés", color: "error" as const };
      case "maladie":
        return { label: "Maladie", color: "error" as const };
      default:
        return { label: state, color: "default" as const }; // 'indisponible' générique
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "grey.50",
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 2, sm: 3 },
            mb: { xs: 3, md: 4 },
          }}
        >
          <BackButton fallbackPath="/assignments" label="Retour" />
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "1.5rem", sm: "1.875rem", md: "2.25rem" },
              color: "text.primary",
            }}
          >
            Nouvelle affectation
          </Typography>
        </Box>

        <Paper
          elevation={1}
          sx={{
            bgcolor: "white",
            borderRadius: 2,
            p: { xs: 2, sm: 3, md: 4 },
            mb: { xs: 3, md: 4 },
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.875rem" },
              color: "text.primary",
              mb: { xs: 2, md: 3 },
            }}
          >
            Informations de l'affectation
          </Typography>

          {/* Stepper de progression */}
          <Box sx={{ mb: { xs: 3, md: 4 } }}>
            <Stepper 
              activeStep={
                (selectedVehicle ? 1 : 0) + 
                (selectedDriver ? 1 : 0) + 
                (formData.date && formData.time ? 1 : 0) + 
                (formData.startMileage && formData.startFuelLevel ? 1 : 0)
              }
              sx={{ 
                "& .MuiStepLabel-root": { 
                  fontSize: { xs: "0.7rem", sm: "0.875rem" } 
                } 
              }}
            >
              <Step>
                <StepLabel>Véhicule</StepLabel>
              </Step>
              <Step>
                <StepLabel>Chauffeur</StepLabel>
              </Step>
              <Step>
                <StepLabel>Date/Heure</StepLabel>
              </Step>
              <Step>
                <StepLabel>Kilométrage/Carburant</StepLabel>
              </Step>
            </Stepper>
          </Box>

          {/* Cartes de prévisualisation */}
          {(selectedVehicle || selectedDriver) && (
            <Fade in={true}>
              <Box sx={{ 
                display: "grid", 
                gridTemplateColumns: { xs: "1fr", md: selectedVehicle && selectedDriver ? "1fr 1fr" : "1fr" },
                gap: 2,
                mb: { xs: 3, md: 4 }
              }}>
                {selectedVehicle && (
                  <Zoom in={true}>
                    <Card 
                      elevation={2}
                      sx={{ 
                        bgcolor: "primary.50",
                        border: "2px solid",
                        borderColor: "primary.main",
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: 4,
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                          <Box component="img" src="/vehicule-icon.png" sx={{ width: 24, height: 24 }} />
                          <Typography variant="subtitle1" fontWeight={600} color="primary.main">
                            Véhicule sélectionné
                          </Typography>
                          <Chip 
                            icon={<CheckCircleIcon />}
                            label="✓"
                            size="small"
                            color="success"
                            sx={{ ml: "auto" }}
                          />
                        </Box>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                          {selectedVehicle.plate}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedVehicle.brand} {selectedVehicle.model}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
                          <Chip 
                            label={getVehicleStateLabel(selectedVehicle.operationalState).label}
                            color={getVehicleStateLabel(selectedVehicle.operationalState).color}
                            size="small"
                          />
                          {selectedVehicle.vehicleType && (
                            <Chip 
                              label={VEHICLE_TYPE_LABELS[selectedVehicle.vehicleType as VehicleType] || selectedVehicle.vehicleType}
                              variant="outlined"
                              size="small"
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Zoom>
                )}
                {selectedDriver && (
                  <Zoom in={true}>
                    <Card 
                      elevation={2}
                      sx={{ 
                        bgcolor: "success.50",
                        border: "2px solid",
                        borderColor: "success.main",
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: 4,
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                          <PeopleIcon color="success" />
                          <Typography variant="subtitle1" fontWeight={600} color="success.main">
                            Chauffeur sélectionné
                          </Typography>
                          <Chip 
                            icon={<CheckCircleIcon />}
                            label="✓"
                            size="small"
                            color="success"
                            sx={{ ml: "auto" }}
                          />
                        </Box>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                          {selectedDriver.firstName} {selectedDriver.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ID: {selectedDriver.driverId} • Permis {selectedDriver.licenseType}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
                          <Chip 
                            label={getDriverStateLabel(selectedDriver.status).label}
                            color={getDriverStateLabel(selectedDriver.status).color}
                            size="small"
                          />
                          {selectedVehicle && isDriverCompatible(selectedDriver) && (
                            <Chip 
                              label="✓ Compatible"
                              color="success"
                              size="small"
                              icon={<CheckCircleIcon />}
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Zoom>
                )}
              </Box>
            </Fade>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 4, md: 6 } }}>
            {/* Section Véhicule */}
            <Box>
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  flexDirection: { xs: "column", sm: "row" },
                  flexWrap: "wrap",
                  gap: { xs: 1.5, sm: 2 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="medium"
                    sx={{
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      color: "text.primary",
                    }}
                >
                  Véhicule *
                </Typography>
                  {selectedVehicle && (
                    <Chip 
                      icon={<CheckCircleIcon />}
                      label="Sélectionné"
                      size="small"
                      color="success"
                      sx={{ ml: 0.5 }}
                    />
                  )}
                  <Tooltip title="Scanner QR code véhicule">
                    <IconButton
                      size={isMobile ? "small" : "medium"}
                      color="primary"
                      onClick={() => setVehicleScanOpen(true)}
                      sx={{
                        bgcolor: "primary.50",
                        "&:hover": { bgcolor: "primary.100", transform: "scale(1.1)" },
                        transition: "all 0.2s",
                      }}
                    >
                      <QrCode size={isMobile ? 16 : 18} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Le véhicule doit être disponible pour être affecté">
                    <IconButton size="small" sx={{ color: "text.secondary" }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Stack 
                  direction={{ xs: "column", sm: "row" }} 
                  spacing={{ xs: 1, sm: 2 }} 
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        size={isMobile ? "small" : "medium"}
                        checked={vehicleFilterStatus === "available"}
                        onChange={(e) =>
                          setVehicleFilterStatus(
                            e.target.checked ? "available" : "all"
                          )
                        }
                      />
                    }
                    label={
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                      >
                        {isMobile ? "Disponibles" : "Disponibles uniquement"}
                      </Typography>
                    }
                  />
                  <FormControl
                    size="small"
                    variant="standard"
                    sx={{ minWidth: { xs: "100%", sm: 120 } }}
                  >
                    <InputLabel sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                      Trier par
                    </InputLabel>
                    <Select
                      value={vehicleSort}
                      onChange={(e) =>
                        setVehicleSort(e.target.value as "plate" | "brand")
                      }
                      label="Trier par"
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      <MenuItem value="plate" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        Immatriculation
                      </MenuItem>
                      <MenuItem value="brand" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        Marque / Modèle
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Box>

              {/* Accès rapide - Véhicules récents */}
              {recentItems.vehicles.length > 0 && !selectedVehicle && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ 
                      mb: 1, 
                      display: "block",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" }
                    }}
                  >
                    ⚡ Accès rapide - Récents
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: { xs: 0.5, sm: 1 } }}>
                    {recentItems.vehicles.slice(0, 3).map((v) => {
                      const vehicle = vehicles.find(
                        (veh) => veh.plate === v.plate
                      );
                      if (!vehicle) return null;
                      return (
                        <Chip
                          key={v.plate}
                          label={`${v.plate} ${v.brand ? `• ${v.brand}` : ""}`}
                          onClick={() => handleVehicleChange(null, vehicle)}
                          clickable
                          color="primary"
                          variant="outlined"
                          size="small"
                          sx={{ mb: 1 }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {loadingVehicles && (
                <Box sx={{ mb: 1 }}>
                  <LinearProgress sx={{ borderRadius: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    Chargement des véhicules...
                  </Typography>
                </Box>
              )}
              <Autocomplete
                options={filteredVehicles}
                getOptionLabel={(option) =>
                  `${option.plate} - ${option.brand} ${option.model}`
                }
                value={selectedVehicle}
                onChange={handleVehicleChange}
                disabled={loadingVehicles}
                getOptionDisabled={(option) =>
                  option.operationalState !== "disponible"
                }
                loading={loadingVehicles}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={selectedVehicle ? "Véhicule sélectionné" : "Rechercher un véhicule (Plaque, Marque...)"}
                    variant="outlined"
                    required
                    error={
                      (!selectedVehicle && submitting) ||
                      (selectedVehicle &&
                        selectedVehicle.operationalState !== "disponible")
                    }
                    helperText={
                      selectedVehicle && selectedVehicle.operationalState !== "disponible"
                        ? `⛔ Ce véhicule n'est pas disponible (Statut : ${
                            getVehicleStateLabel(
                              selectedVehicle.operationalState
                            ).label
                          })`
                        : selectedVehicle
                        ? "✓ Véhicule valide et disponible"
                        : "Sélectionnez un véhicule disponible"
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        transition: "all 0.3s ease",
                        ...(selectedVehicle && {
                          bgcolor: "primary.50",
                          borderColor: "primary.main",
                        }),
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  const stateInfo = getVehicleStateLabel(
                    option.operationalState
                  );
                  return (
                    <li key={key} {...otherProps}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {option.plate}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.brand} {option.model}
                          </Typography>
                        </Box>
                        <Chip
                          label={stateInfo.label}
                          color={stateInfo.color}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </li>
                  );
                }}
                noOptionsText="Aucun véhicule trouvé"
              />
            </Box>

            {/* Section Chauffeur */}
            <Box>
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  flexDirection: { xs: "column", sm: "row" },
                  flexWrap: "wrap",
                  gap: { xs: 1.5, sm: 2 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="medium"
                    sx={{
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      color: "text.primary",
                    }}
                >
                  Chauffeur *
                </Typography>
                  {selectedDriver && (
                    <Chip 
                      icon={<CheckCircleIcon />}
                      label="Sélectionné"
                      size="small"
                      color="success"
                      sx={{ ml: 0.5 }}
                    />
                  )}
                  <Tooltip title="Scanner QR code chauffeur">
                    <IconButton
                      size={isMobile ? "small" : "medium"}
                      color="success"
                      onClick={() => setDriverScanOpen(true)}
                      sx={{
                        bgcolor: "success.50",
                        "&:hover": { bgcolor: "success.100", transform: "scale(1.1)" },
                        transition: "all 0.2s",
                      }}
                    >
                      <QrCode size={isMobile ? 16 : 18} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Le chauffeur doit être disponible et avoir un permis compatible">
                    <IconButton size="small" sx={{ color: "text.secondary" }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Stack 
                  direction={{ xs: "column", sm: "row" }} 
                  spacing={{ xs: 1, sm: 2 }} 
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        size={isMobile ? "small" : "medium"}
                        checked={driverFilterStatus === "available"}
                        onChange={(e) =>
                          setDriverFilterStatus(
                            e.target.checked ? "available" : "all"
                          )
                        }
                      />
                    }
                    label={
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                      >
                        {isMobile ? "Disponibles" : "Disponibles uniquement"}
                      </Typography>
                    }
                  />
                  <FormControl
                    size="small"
                    variant="standard"
                    sx={{ minWidth: { xs: "100%", sm: 120 } }}
                  >
                    <InputLabel sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                      Trier par
                    </InputLabel>
                    <Select
                      value={driverSort}
                      onChange={(e) =>
                        setDriverSort(e.target.value as "name" | "id")
                      }
                      label="Trier par"
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      <MenuItem value="name" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        Nom
                      </MenuItem>
                      <MenuItem value="id" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        Identifiant
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Box>

              {/* Accès rapide - Chauffeurs récents */}
              {recentItems.drivers.length > 0 && !selectedDriver && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: "block" }}
                  >
                    ⚡ Accès rapide - Récents
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {recentItems.drivers.slice(0, 3).map((d) => {
                      const driver = drivers.find((drv) => drv._id === d.id);
                      if (!driver) return null;
                      const isCompatible =
                        !selectedVehicle?.vehicleType ||
                        isDriverCompatible(driver);
                      return (
                        <Chip
                          key={d.id}
                          label={d.name}
                          onClick={() =>
                            isCompatible && handleDriverChange(null, driver)
                          }
                          clickable={isCompatible}
                          color={isCompatible ? "success" : "default"}
                          variant="outlined"
                          size="small"
                          disabled={!isCompatible}
                          sx={{ mb: 1 }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {/* Alerte informative sur le type de véhicule sélectionné */}
              {selectedVehicle?.vehicleType && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Véhicule sélectionné :</strong>{" "}
                    {VEHICLE_TYPE_LABELS[
                      selectedVehicle.vehicleType as VehicleType
                    ] || selectedVehicle.vehicleType}
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      Seuls les chauffeurs avec un permis compatible peuvent
                      être affectés à ce véhicule.
                    </Typography>
                  </Typography>
                </Alert>
              )}

              {loadingDrivers && (
                <Box sx={{ mb: 1 }}>
                  <LinearProgress sx={{ borderRadius: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    Chargement des chauffeurs...
                  </Typography>
                </Box>
              )}
              <Autocomplete
                options={filteredDrivers}
                getOptionLabel={(option) =>
                  `${option.firstName} ${option.lastName} ${
                    option.driverId ? `(${option.driverId})` : ""
                  }`
                }
                value={selectedDriver}
                onChange={handleDriverChange}
                disabled={loadingDrivers}
                loading={loadingDrivers}
                getOptionDisabled={(option) =>
                  option.status !== "disponible" || !isDriverCompatible(option)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={selectedDriver ? "Chauffeur sélectionné" : "Rechercher un chauffeur (Nom, ID...)"}
                    variant="outlined"
                    required
                    helperText={
                      selectedDriver
                        ? selectedVehicle && !isDriverCompatible(selectedDriver)
                          ? "⚠️ Permis incompatible avec le type de véhicule"
                          : "✓ Chauffeur valide et disponible"
                        : "Sélectionnez un chauffeur disponible"
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        transition: "all 0.3s ease",
                        ...(selectedDriver && {
                          bgcolor: "success.50",
                          borderColor: "success.main",
                        }),
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  const stateInfo = getDriverStateLabel(option.status);
                  const compatible = isDriverCompatible(option);
                  const vehicleTypeLabel = selectedVehicle?.vehicleType
                    ? VEHICLE_TYPE_LABELS[
                        selectedVehicle.vehicleType as VehicleType
                      ] || selectedVehicle.vehicleType
                    : null;

                  return (
                    <Tooltip
                      key={key}
                      title={
                        !compatible
                          ? `Permis ${option.licenseType} incompatible avec ${vehicleTypeLabel}`
                          : ""
                      }
                      placement="left"
                    >
                      <li
                        {...otherProps}
                        style={{
                          ...((otherProps as any).style || {}),
                          opacity: compatible ? 1 : 0.5,
                          backgroundColor: compatible ? undefined : "#f5f5f5",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                            alignItems: "center",
                          }}
                        >
                          <Box>
                            <Typography
                              variant="body1"
                              fontWeight="medium"
                              sx={{
                                color: compatible ? "inherit" : "text.disabled",
                              }}
                            >
                              {option.firstName} {option.lastName}
                            </Typography>
                            <Typography
                              variant="body2"
                              color={
                                compatible ? "text.secondary" : "text.disabled"
                              }
                            >
                              {option.driverId || "Pas d'ID"} • Permis{" "}
                              {option.licenseType}
                            </Typography>
                            {!compatible && (
                              <Typography variant="caption" color="error">
                                ⚠️ Permis incompatible
                              </Typography>
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <Chip
                              label={`Permis ${option.licenseType}`}
                              size="small"
                              variant="outlined"
                              color={compatible ? "primary" : "default"}
                            />
                            <Chip
                              label={stateInfo.label}
                              color={stateInfo.color}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                      </li>
                    </Tooltip>
                  );
                }}
                noOptionsText="Aucun chauffeur trouvé"
              />
            </Box>

            <Divider sx={{ my: { xs: 3, md: 4 } }} />

            {/* Section Équipement de manutention (optionnelle) */}
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ 
                  mb: { xs: 1.5, md: 2 }, 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 1,
                  fontSize: { xs: "0.75rem", sm: "0.875rem" }
                }}
              >
                <img
                  src="/equipements/chariot-elevateur.png"
                  alt=""
                  style={{ width: isMobile ? 18 : 20, height: isMobile ? 18 : 20 }}
                />
                {isMobile ? "Équipement (optionnel)" : "Équipement de manutention (optionnel)"}
              </Typography>

              {selectedEquipment ? (
                // Équipement sélectionné
                <Paper
                  elevation={0}
                  sx={{
                    border: "2px solid #22C55E",
                    borderRadius: 3,
                    overflow: "hidden",
                    background:
                      "linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)",
                  }}
                >
                  <Box
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 1.5, sm: 2 },
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 48, sm: 56 },
                        height: { xs: 48, sm: 56 },
                        bgcolor: "white",
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      }}
                    >
                      <img
                        src={
                          EQUIPMENT_IMAGES[selectedEquipment.equipmentType] ||
                          "/equipements/diable.png"
                        }
                        alt=""
                        style={{ 
                          width: isMobile ? 32 : 38, 
                          height: isMobile ? 32 : 38, 
                          objectFit: "contain" 
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          color="#166534"
                        >
                          ✓ {selectedEquipment.internalNumber}
                        </Typography>
                        <Chip
                          label="Sélectionné"
                          size="small"
                          color="success"
                          sx={{ height: 20, fontSize: "0.65rem" }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {selectedEquipment.brand} {selectedEquipment.model}
                      </Typography>
                      {selectedEquipment.location && (
                        <Typography variant="caption" color="text.secondary">
                          📍 {selectedEquipment.location}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setShowEquipmentSection(true)}
                        sx={{ borderRadius: 2, minWidth: "auto", px: 2 }}
                      >
                        Changer
                      </Button>
                      <IconButton
                        onClick={() => setSelectedEquipment(null)}
                        size="small"
                        sx={{
                          bgcolor: "#FEE2E2",
                          color: "#DC2626",
                          "&:hover": { bgcolor: "#FECACA" },
                        }}
                      >
                        <X size={16} />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              ) : (
                // Aucun équipement - bouton d'ajout
                <Paper
                  onClick={() => setShowEquipmentSection(true)}
                  elevation={0}
                  sx={{
                    border: "2px dashed #CBD5E1",
                    borderRadius: 3,
                    p: { xs: 2, sm: 3 },
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: { xs: 1.5, sm: 2 },
                    flexDirection: { xs: "column", sm: "row" },
                    "&:hover": {
                      borderColor: "#3B82F6",
                      bgcolor: "#F8FAFC",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 40, sm: 48 },
                      height: { xs: 40, sm: 48 },
                      bgcolor: "#F1F5F9",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src="/equipements/diable.png"
                      alt=""
                      style={{ 
                        width: isMobile ? 24 : 28, 
                        height: isMobile ? 24 : 28, 
                        opacity: 0.6 
                      }}
                    />
                  </Box>
                  <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                    <Typography
                      variant="body1"
                      fontWeight={500}
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                    >
                      Ajouter un équipement
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                    >
                      Diable, transpalette, chariot...
                    </Typography>
                  </Box>
                  <Chip
                    label="+ Ajouter"
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      bgcolor: "#3B82F6",
                      color: "white",
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      "&:hover": { bgcolor: "#2563EB" },
                    }}
                  />
                </Paper>
              )}
            </Box>

            {/* Dialog de sélection d'équipement */}
            <Dialog
              open={showEquipmentSection}
              onClose={() => setShowEquipmentSection(false)}
              maxWidth="md"
              fullWidth
              fullScreen={isMobile}
              PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
            >
              <DialogTitle
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: "#F8FAFC",
                  borderBottom: "1px solid #E2E8F0",
                  py: 2.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: "#3B82F6",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src="/equipements/chariot-elevateur.png"
                      alt=""
                      style={{
                        width: 32,
                        height: 32,
                        filter: "brightness(0) invert(1)",
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      Sélectionner un équipement
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {equipments.length} équipement
                      {equipments.length > 1 ? "s" : ""} disponible
                      {equipments.length > 1 ? "s" : ""}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={() => setShowEquipmentSection(false)}
                  sx={{ bgcolor: "#F1F5F9" }}
                >
                  <X size={20} />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ pt: 3, pb: 4 }}>
                {loadingEquipments ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary">
                      Chargement des équipements...
                    </Typography>
                  </Box>
                ) : equipments.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        bgcolor: "#FEF3C7",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                      }}
                    >
                      <img
                        src="/equipements/diable.png"
                        alt=""
                        style={{ width: 40, height: 40, opacity: 0.6 }}
                      />
                    </Box>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Aucun équipement disponible
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      Créez d'abord des équipements dans la gestion du parc
                    </Typography>
                    <Button
                      variant="contained"
                      href="/equipements-manutention"
                      sx={{ borderRadius: 2 }}
                    >
                      Gérer les équipements
                    </Button>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(auto-fill, minmax(250px, 1fr))",
                        md: "repeat(auto-fill, minmax(280px, 1fr))",
                      },
                      gap: { xs: 1.5, sm: 2 },
                    }}
                  >
                    {equipments.map((eq) => (
                      <Paper
                        key={eq._id}
                        onClick={() => {
                          setSelectedEquipment(eq);
                          setShowEquipmentSection(false);
                        }}
                        elevation={0}
                        sx={{
                          p: 2.5,
                          border: "2px solid #E2E8F0",
                          borderRadius: 2.5,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          position: "relative",
                          overflow: "hidden",
                          "&:hover": {
                            borderColor: "#3B82F6",
                            bgcolor: "#EFF6FF",
                            transform: "translateY(-3px)",
                            boxShadow: "0 8px 24px rgba(59, 130, 246, 0.2)",
                          },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            bgcolor: "#22C55E",
                            opacity: 0.8,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              bgcolor: "#F1F5F9",
                              borderRadius: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={
                                EQUIPMENT_IMAGES[eq.equipmentType] ||
                                "/equipements/diable.png"
                              }
                              alt=""
                              style={{
                                width: 40,
                                height: 40,
                                objectFit: "contain",
                              }}
                            />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              noWrap
                            >
                              {eq.internalNumber}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                            >
                              {eq.brand} {eq.model}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                mt: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <Chip
                                label="Disponible"
                                size="small"
                                color="success"
                                sx={{ height: 22, fontSize: "0.7rem" }}
                              />
                              {eq.location && (
                                <Chip
                                  label={eq.location}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 22, fontSize: "0.7rem" }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}
              </DialogContent>
            </Dialog>

            <Divider sx={{ my: { xs: 3, md: 4 } }} />

            {/* Section Date/Heure avec bouton Maintenant */}
            <Box>
              <Box sx={{ 
                display: "flex", 
                flexWrap: "wrap", 
                alignItems: "center", 
                gap: { xs: 1, sm: 2 },
                mb: { xs: 2, md: 3 }
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="subtitle1"
                fontWeight="medium"
                    sx={{
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      color: "text.primary",
                    }}
              >
                Date et Heure
              </Typography>
                  {formData.date && formData.time && (
                    <Chip 
                      icon={<CheckCircleIcon />}
                      label="Définie"
                      size="small"
                      color="success"
                    />
                  )}
                </Box>
              <Chip
                label="⚡ Maintenant"
                onClick={() => {
                    const now = getCurrentDateTime();
                  setFormData((prev) => ({
                    ...prev,
                      date: now.date,
                      time: now.time,
                  }));
                    enqueueSnackbar("Date et heure définies à maintenant", { variant: "success" });
                }}
                clickable
                color="primary"
                  size={isMobile ? "small" : "medium"}
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ 
              display: "grid", 
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 2, md: 3 }
            }}>
              <Box>
                <Typography
                  component="label"
                  variant="body2"
                  fontWeight="medium"
                  sx={{ 
                    display: "block",
                    mb: 1,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    color: "text.primary"
                  }}
                >
                  Date *
                </Typography>
                <TextField
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box>
                <Typography
                  component="label"
                  variant="body2"
                  fontWeight="medium"
                  sx={{ 
                    display: "block",
                    mb: 1,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    color: "text.primary"
                  }}
                >
                  Heure *
                </Typography>
                <TextField
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>

            <Box>
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 1,
                mb: { xs: 2, md: 3 }
              }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="medium"
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "text.primary",
                  }}
                >
                  Informations de départ
                </Typography>
                {formData.startMileage && formData.startFuelLevel && (
                  <Chip 
                    icon={<CheckCircleIcon />}
                    label="Complété"
                    size="small"
                    color="success"
                  />
                )}
                {selectedVehicle && (
                  <Tooltip title="Les valeurs sont pré-remplies depuis la dernière affectation de ce véhicule">
                    <InfoIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ 
                display: "grid", 
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: { xs: 2, md: 3 }
              }}>
                <Box>
                  <Typography
                    component="label"
                    variant="body2"
                    fontWeight="medium"
                    sx={{ 
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 1,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      color: "text.primary"
                    }}
                  >
                    <SpeedIcon fontSize="small" />
                  Kilométrage initial *
                  </Typography>
                  <TextField
                  type="number"
                  name="startMileage"
                  value={formData.startMileage}
                  onChange={handleInputChange}
                  required
                    fullWidth
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      endAdornment: <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>km</Typography>
                    }}
                    helperText={selectedVehicle && formData.startMileage > 0 ? "✓ Valeur pré-remplie depuis l'historique" : ""}
                  />
                </Box>
                <Box>
                  <Typography
                    component="label"
                    variant="body2"
                    fontWeight="medium"
                    sx={{ 
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 1,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      color: "text.primary"
                    }}
                  >
                    <FuelLevelIcon fontSize="small" />
                  Niveau de carburant initial (%) *
                  </Typography>
                  <TextField
                  type="number"
                  name="startFuelLevel"
                  value={formData.startFuelLevel}
                  onChange={handleInputChange}
                  required
                    fullWidth
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: 0, max: 100 }}
                    InputProps={{
                      endAdornment: <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>%</Typography>
                    }}
                    helperText={
                      formData.startFuelLevel >= 0 && formData.startFuelLevel <= 100
                        ? selectedVehicle && formData.startFuelLevel > 0
                          ? "✓ Valeur pré-remplie depuis l'historique"
                          : ""
                        : "⚠️ Valeur entre 0 et 100%"
                    }
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Indicateur de validation */}
        {!isFormValid() && (
          <Fade in={true}>
            <Alert 
              severity="info" 
              icon={<InfoIcon />}
              sx={{ 
                mb: { xs: 2, md: 3 },
                "& .MuiAlert-message": {
                  fontSize: { xs: "0.75rem", sm: "0.875rem" }
                }
              }}
            >
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                Formulaire incomplet
              </Typography>
              <Typography variant="caption" component="div">
                {!selectedVehicle && "• Sélectionnez un véhicule "}
                {!selectedDriver && "• Sélectionnez un chauffeur "}
                {(!formData.date || !formData.time) && "• Définissez la date et l'heure "}
                {(!formData.startMileage || !formData.startFuelLevel) && "• Renseignez le kilométrage et le carburant"}
              </Typography>
            </Alert>
          </Fade>
        )}

        {/* Boutons d'action - version desktop */}
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flexDirection: { sm: "row" },
            justifyContent: "space-between",
            gap: { xs: 2, sm: 3 },
            mt: { xs: 2, md: 3 },
          }}
        >
          <Button
            onClick={() => navigate("/assignments")}
            variant="outlined"
            size={isMobile ? "medium" : "large"}
            sx={{
              minWidth: { xs: "100%", sm: 150 },
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !isFormValid()}
            variant="contained"
            size={isMobile ? "medium" : "large"}
            startIcon={isFormValid() ? <CheckCircleIcon /> : undefined}
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              fontSize: { xs: "0.875rem", sm: "1rem" },
              ...(isFormValid() && {
                background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                },
              }),
            }}
          >
            {submitting ? "Création en cours..." : "Créer l'affectation"}
          </Button>
        </Box>

        {/* Mode Express - Validation rapide quand véhicule + chauffeur sélectionnés */}
        {selectedVehicle && selectedDriver && isFormValid() && (
          <Box
            sx={{
              display: { xs: "block", sm: "none" },
              position: "fixed",
              bottom: { xs: 80, sm: 0 },
              left: { xs: 8, sm: 0 },
              right: { xs: 8, sm: 0 },
              zIndex: 40,
            }}
          >
            <Paper
              elevation={8}
              sx={{
                bgcolor: "success.50",
                border: "2px solid",
                borderColor: "success.main",
                borderRadius: 3,
                p: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} color="success.dark">
                    ⚡ Mode Express
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {selectedVehicle.plate} • {selectedDriver.firstName}{" "}
                    {selectedDriver.lastName}
                  </Typography>
                </Box>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  variant="contained"
                  color="success"
                  size="small"
                  sx={{ minWidth: 100 }}
                >
                  {submitting ? "..." : "✓ Valider"}
                </Button>
              </Box>
            </Paper>
          </Box>
        )}

        {/* Bouton flottant mobile - toujours visible */}
        <Box
          sx={{
            display: { xs: "block", sm: "none" },
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 50,
            bgcolor: "white",
            borderTop: 1,
            borderColor: "divider",
            p: 1.5,
            boxShadow: 3,
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              onClick={() => navigate("/assignments")}
              variant="outlined"
              fullWidth
              size="medium"
              sx={{ fontSize: "0.875rem" }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !isFormValid()}
              variant="contained"
              fullWidth
              size="medium"
              sx={{ fontSize: "0.875rem" }}
            >
              {submitting
                ? "Création..."
                : isFormValid()
                ? "✓ Créer"
                : "Compléter"}
            </Button>
          </Box>
        </Box>

        {/* Spacer pour le bouton flottant sur mobile */}
        <Box sx={{ display: { xs: "block", sm: "none" }, height: 80 }} />
      </Container>

      {/* Dialog Scanner Plaque Véhicule (OCR) ou QR Code */}
      <Dialog
        open={vehicleScanOpen}
        onClose={() => setVehicleScanOpen(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: "black" } }}
      >
        <QRCodeScanner
          onScan={handleVehiclePlateScanned}
          onClose={() => setVehicleScanOpen(false)}
          title="Scanner Véhicule"
          placeholder="Ex: AB-123-CD"
        />
      </Dialog>

      {/* Dialog Scanner QR Chauffeur */}
      <Dialog
        open={driverScanOpen}
        onClose={() => setDriverScanOpen(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: "black" } }}
      >
        <QRCodeScanner
          onScan={handleDriverIdScanned}
          onClose={() => setDriverScanOpen(false)}
          title="Scanner Chauffeur"
          placeholder="Ex: DRV001"
        />
      </Dialog>
    </Box>
  );
};

export default AssignmentForm;
